import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import billingService from '../../api/billingService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getUser } from '../../Utility/Utility';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const QuoteView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const quoteRef = useRef();

  useEffect(() => {
    fetchQuote();
    const userData = getUser();
    if (userData) setUser(userData);
  }, [id]);

  const fetchQuote = async () => {
    try {
      const res = await billingService.getQuoteById(id);
      if (res.success) setQuote(res.data);
    } catch (error) {
      console.error('Error fetching quote:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    const element = quoteRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${quote.quotationNumber}.pdf`);
  };

  if (loading) return <div className="p-5 text-center">Loading quotation...</div>;
  if (!quote) return <div className="p-5 text-center text-danger">Quotation not found.</div>;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 no-print">
        <div>
        
          <h4 className="fw-700 mb-0">Quotation Detail</h4>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-light px-4" onClick={() => window.print()}>
            <i className="bi bi-printer me-2"></i> Print
          </button>
          <button className="btn btn-primary px-4" onClick={downloadPDF}>
            <i className="bi bi-file-pdf me-2"></i> Download PDF
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mx-auto" style={{ maxWidth: '900px' }} ref={quoteRef}>
        <div className="card-body p-5">
          {/* Header */}
          <div className="row mb-5">
            <div className="col-6">
              <h2 className="fw-800 text-primary mb-1">QUOTATION</h2>
              <div className="text-muted">#{quote.quotationNumber}</div>
            </div>
            <div className="col-6 text-end">
              <h5 className="fw-700 mb-1">{user?.company || 'BeTyphon'}</h5>
              <div className="text-xs text-muted">
                {user?.email}<br />
                {user?.phone || ''}
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="row mb-5">
            <div className="col-6">
              <h6 className="text-muted text-xs fw-700 uppercase mb-3">QUOTATION FOR</h6>
              <h5 className="fw-700 mb-1">{quote.customerName}</h5>
              <div className="text-xs text-muted">
                {quote.customerEmail && <div>{quote.customerEmail}</div>}
                {quote.customerPhone && <div>{quote.customerPhone}</div>}
                {quote.customerAddress && <div className="mt-1" style={{ maxWidth: '250px' }}>{quote.customerAddress}</div>}
                {quote.gstNumber && <div className="mt-1 fw-600">GSTIN: {quote.gstNumber}</div>}
              </div>
            </div>
            <div className="col-6 text-end">
              <div className="mb-3">
                <h6 className="text-muted text-xs fw-700 uppercase mb-1">QUOTATION DATE</h6>
                <div className="fw-600">{formatDate(quote.quotationDate)}</div>
              </div>
              <div>
                <h6 className="text-muted text-xs fw-700 uppercase mb-1">VALID UNTIL</h6>
                <div className="fw-600 text-danger">{formatDate(quote.validUntil)}</div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive mb-5">
            <table className="table table-borderless align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="text-xs fw-700 py-3 ps-4" style={{ width: '40%' }}>PRODUCT & DESCRIPTION</th>
                  <th className="text-xs fw-700 py-3 text-center">QTY</th>
                  <th className="text-xs fw-700 py-3 text-end">PRICE</th>
                  <th className="text-xs fw-700 py-3 text-end">DISC %</th>
                  <th className="text-xs fw-700 py-3 text-end pe-4">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {quote.items?.map((item, index) => (
                  <tr key={index} className="border-bottom">
                    <td className="py-4 ps-4">
                      <div className="fw-700 mb-1">{item.productName}</div>
                      <div className="text-xs text-muted" style={{ whiteSpace: 'pre-line' }}>
                        {item.description}
                      </div>
                    </td>
                    <td className="text-center fw-600">{item.quantity} {item.units}</td>
                    <td className="text-end fw-600">{formatCurrency(item.sellingPrice)}</td>
                    <td className="text-end fw-600">{item.discount}%</td>
                    <td className="text-end fw-700 pe-4">{formatCurrency(item.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="row justify-content-end mb-5">
            <div className="col-md-5">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Sub Total</span>
                <span className="fw-600">{formatCurrency(quote.subTotal)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Total Discount</span>
                <span className="fw-600 text-danger">-{formatCurrency(quote.totalDiscounts)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">CGST ({(quote.items[0]?.gstRate/2) || 9}%)</span>
                <span className="fw-600">{formatCurrency(quote.cgstTotal)}</span>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span className="text-muted">SGST ({(quote.items[0]?.gstRate/2) || 9}%)</span>
                <span className="fw-600">{formatCurrency(quote.sgstTotal)}</span>
              </div>
              <div className="d-flex justify-content-between p-3 bg-primary rounded-3 text-white">
                <span className="fw-700 h5 mb-0">Grand Total</span>
                <span className="fw-700 h5 mb-0">{formatCurrency(quote.totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="border-top pt-4">
            <h6 className="text-muted text-xs fw-700 uppercase mb-2">TERMS & CONDITIONS</h6>
            <div className="text-xs text-muted" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
              {quote.notes}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-5 pt-5 text-center text-xs text-muted">
            This is a computer-generated quotation.
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteView;